import { useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';

// Company details come from the server (Admin > Company Details), so a change made by an
// admin shows everywhere: the admin's own pages update immediately, and everyone else's open
// pages pick it up within 30 seconds or as soon as they return to the tab.

// appUrl: the address people open the app at (links in emails, WhatsApp, SMS); effectiveAppUrl: the one in use
const COMPANY_FIELDS = 'name brandName address phone email website gstin cin appUrl effectiveAppUrl appUrlIsLocal updatedAt';

export const COMPANY_DETAILS_QUERY = gql`
  query CompanyDetails {
    companyDetails { ${COMPANY_FIELDS} }
  }
`;

export const UPDATE_COMPANY_DETAILS_MUTATION = gql`
  mutation UpdateCompanyDetails($input: CompanyDetailsInput!) {
    updateCompanyDetails(input: $input) { ${COMPANY_FIELDS} }
  }
`;

// Shown only until the server's details have loaded
export const COMPANY_DEFAULTS = {
  name:      'Matrimony Services Pvt. Ltd.',
  brandName: 'Matrimony',
  address:   '',
  phone:     '',
  email:     '',
  website:   '',
  gstin:     '',
  cin:       '',
  appUrl:    '',
};

const REFRESH_MS = 30 * 1000;

export function useCompanyDetails() {
  const { data, refetch } = useQuery(COMPANY_DETAILS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: REFRESH_MS,
  });
  // Pick up changes straight away when the user comes back to this tab
  useEffect(() => {
    const onFocus = () => { refetch().catch(() => {}); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);
  const company = { ...COMPANY_DEFAULTS, ...(data?.companyDetails || {}) };
  if (!company.brandName) company.brandName = company.name;
  return company;
}

// Saves to the server; every component using useCompanyDetails() re-renders with the result
export function useSaveCompanyDetails() {
  const [save, state] = useMutation(UPDATE_COMPANY_DETAILS_MUTATION, {
    update(cache, { data }) {
      cache.writeQuery({ query: COMPANY_DETAILS_QUERY, data: { companyDetails: data.updateCompanyDetails } });
    },
    // The marketing card warns while links would point at this computer
    refetchQueries: ['MarketingSettings', 'VendorLeadStats'],
  });
  const saveDetails = (details) => save({
    variables: {
      input: {
        name: details.name, brandName: details.brandName || '', address: details.address || '', phone: details.phone || '',
        email: details.email || '', website: details.website || '', gstin: details.gstin || '', cin: details.cin || '',
        appUrl: details.appUrl || '',
      },
    },
  });
  return [saveDetails, state];
}

// Details saved by the old version of this page only in this browser, if any
const LEGACY_KEY = 'matrimony_company_details';
export function getLegacyBrowserDetails() {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function clearLegacyBrowserDetails() {
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* ignore */ }
}

// Keeps the browser tab title and description in step with the company's display name
export function CompanyDocumentTitle() {
  const company = useCompanyDetails();
  useEffect(() => {
    document.title = company.brandName;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', `${company.brandName} - Bringing Families Together`);
  }, [company.brandName]);
  return null;
}
